import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Plus,
  ArrowRight,
  MoreVertical,
  MessageSquare,
  MessageCircle,
  Reply,
  Calendar,
  AlertOctagon,
  Flame,
  Brain,
  Sparkles,
  CheckCircle2,
  Circle,
  RefreshCw,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { OWLBERT_DARK } from "@/lib/owlbert";
const OWL_PORTRAIT = OWLBERT_DARK;

function timeOfDay() {
  const h = new Date().getHours();
  if (h < 5) return "late night";
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  if (h < 21) return "evening";
  return "night";
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cronHealth, setCronHealth] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const load = async (fresh = false) => {
    if (fresh) setRefreshing(true);
    else setLoading(true);
    setLoadError(false);
    try {
      const [r, h] = await Promise.all([
        apiGet("/operator/today"),
        apiGet("/operator/cron-health").catch(() => null),
      ]);
      setData(r);
      setCronHealth(h);
    } catch (e) {
      setLoadError(true);
      toast.error("Could not load the client overview.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line
  }, [user]);

  const handleDelete = async (workspaceId, name) => {
    if (!window.confirm(`Delete client "${name}"? This cannot be undone.`)) return;
    try {
      await apiDelete(`/workspaces/${workspaceId}`);
      toast.success("client removed");
      load();
    } catch (e) {
      toast.error("delete failed");
    }
  };

  const totals = data?.totals || {};
  const clients = data?.clients || [];
  const totalActions = totals.total_actions || 0;

  return (
    <Layout
      eyebrow={new Date().toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })}
      title={user?.name ? `Good ${timeOfDay()}, ${user.name.split(" ")[0]}.` : "All Clients"}
      subtitle={
        loading ? "Loading your client overview…"
          : loadError ? "The overview could not be refreshed."
          : totalActions > 0
          ? `${totalActions.toLocaleString()} pending items across ${clients.length} client${clients.length === 1 ? "" : "s"}. Includes earlier work.`
          : "Your pending queues are clear."
      }
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => navigate("/dashboard/chat")}
            className="btn-ghost inline-flex items-center gap-1.5"
            data-testid="operator-chat-btn"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            AI Assistant
          </button>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="btn-ghost inline-flex items-center gap-1.5"
            data-testid="operator-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {user?.role === "admin" && (
            <button
              data-testid="new-workspace-btn"
              onClick={() => navigate("/onboarding")}
              className="btn-primary inline-flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Onboard a client
            </button>
          )}
        </div>
      }
    >
      {loading ? (
        <div className="overline">Loading…</div>
      ) : loadError && !data ? (
        <div role="alert" className="ed-card"><p className="mb-3">We couldn’t load your clients. Your data has not changed.</p><button className="btn-primary" onClick={() => load()}>Try again</button></div>
      ) : clients.length === 0 ? (
        <EmptyState onCreate={() => navigate("/onboarding")} canCreate={user?.role === "admin"} />
      ) : (
        <div className="space-y-8">
          {/* Cron-health alert banner */}
          {cronHealth?.alert && (
            <div
              data-testid="cron-health-banner"
              className="hairline px-5 py-4 flex flex-wrap items-start gap-3"
              style={{
                background: "hsl(var(--accent) / 0.08)",
                borderColor: "hsl(var(--accent) / 0.5)",
              }}
            >
              <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "hsl(var(--accent))" }} />
              <div className="flex-1">
                <div className="overline mb-0.5" style={{ color: "hsl(var(--accent))" }}>
                  Scheduler alert
                </div>
                <div className="text-[13px] text-parchment leading-relaxed">
                  {cronHealth.message}
                </div>
              </div>
              <button
                onClick={() => navigate(clients[0] ? `/workspace/${clients[0].workspace_id}/settings` : "/dashboard")}
                className="btn-ghost text-xs shrink-0"
              >
                Check settings
              </button>
            </div>
          )}

          {/* Top-level KPI strip — editorial borderless tiles */}
          <section className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 fade-up">
            <KpiCell
              icon={MessageSquare}
              label="Pending comments"
              value={totals.pending_comments}
              testid="kpi-pending-comments"
              tone={totals.pending_comments > 0 ? "active" : "muted"}
            />
            <KpiCell
              icon={Reply}
              label="Pending replies"
              value={totals.pending_replies}
              testid="kpi-pending-replies"
              tone={totals.pending_replies > 0 ? "active" : "muted"}
            />
            <KpiCell
              icon={AlertOctagon}
              label="posts overdue"
              value={totals.posts_overdue}
              testid="kpi-posts-overdue"
              tone={totals.posts_overdue > 0 ? "danger" : "muted"}
            />
            <KpiCell
              icon={Calendar}
              label="posts due 7d"
              value={totals.posts_due_soon}
              testid="kpi-posts-due"
              tone={totals.posts_due_soon > 0 ? "active" : "muted"}
            />
            <KpiCell
              icon={Brain}
              label="setup gaps"
              value={totals.clients_setup_incomplete}
              testid="kpi-setup-gaps"
              tone={totals.clients_setup_incomplete > 0 ? "warn" : "muted"}
            />
          </section>

          {/* Client cards — editorial grid */}
          <section data-testid="operator-client-list">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
              <div>
                <div className="chapter-eyebrow mb-2">Client workspaces</div>
                <h2 className="chapter-title" style={{ fontSize: 32 }}>Your clients</h2>
              </div>
              <div className="mono text-[11px] text-[#9ca3af]">
                {data?.generated_at ? `Overview updated ${new Date(data.generated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              {clients.map((c, idx) => (
                <ClientRow
                  key={c.workspace_id}
                  client={c}
                  delay={idx * 60}
                  onOpen={() => navigate(`/workspace/${c.workspace_id}/feed`)}
                  onBrain={() => navigate(`/workspace/${c.workspace_id}/brain`)}
                  onFeed={() => navigate(`/workspace/${c.workspace_id}/feed`)}
                  onReplies={() => navigate(`/workspace/${c.workspace_id}/feed?mode=respond`)}
                  onPipeline={() => navigate(`/workspace/${c.workspace_id}/posts`)}
                  onSetup={() => navigate(`/workspace/${c.workspace_id}/setup`)}
                  onDelete={
                    user?.role === "admin"
                      ? () => handleDelete(c.workspace_id, c.name)
                      : null
                  }
                />
              ))}
            </div>
          </section>
        </div>
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
function KpiCell({ icon: Icon, label, value, testid, tone = "muted" }) {
  const accentColor =
    tone === "active" ? "#111827"
    : tone === "danger" ? "#ef4444"
    : tone === "warn" ? "#f59e0b"
    : "#9ca3af";
  return (
    <div
      className="ed-card p-4 sm:p-5"
      data-testid={testid}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="w-3.5 h-3.5" strokeWidth={2} style={{ color: accentColor }} />
        <span className="stat-label">{label}</span>
      </div>
      <div className="stat-num stat-num-md mt-4" style={{ color: accentColor }}>
        {value ?? 0}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ClientRow({ client, delay, onOpen, onBrain, onFeed, onReplies, onPipeline, onSetup, onDelete }) {
  const c = client;
  const setupComplete = c.brain?.complete;
  const actionCount = (c.pending_comments || 0) + (c.pending_replies || 0) + (c.posts_overdue || 0);

  return (
    <div
      className="ed-card p-4 sm:p-6 fade-up"
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`client-row-${c.workspace_id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0 flex-1">
          <div className="overline">{c.company || "client"}</div>
          <button
            onClick={onOpen}
            className="serif text-2xl text-parchment hover:text-terracotta transition-colors text-left leading-tight mt-1"
            data-testid={`client-name-${c.workspace_id}`}
          >
            {c.name}
          </button>
        </div>
        <div className="flex items-center gap-1">
          {actionCount > 0 && (
            <span
              className="mono text-[10px] uppercase tracking-wider px-2 py-1 text-terracotta border border-terracotta"
              style={{ background: "hsl(var(--accent) / 0.08)" }}
            >
              {actionCount} pending
            </span>
          )}
          {onDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={`Options for ${c.name}`}
                  className="p-1 text-stone-dim hover:text-parchment"
                  data-testid={`client-menu-${c.workspace_id}`}
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-graphite border-hairline">
                <DropdownMenuItem
                  onClick={onSetup}
                  className="text-parchment focus:bg-graphite-2"
                >
                  Resume setup
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-terracotta focus:bg-graphite-2"
                  data-testid={`delete-client-${c.workspace_id}`}
                >
                  Remove client
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Owlbert summary line — the "voice of the day" */}
      {c.owlbert_summary && (
        <details className="mb-5 text-sm text-slate-600">
          <summary className="cursor-pointer">Saved strategy note</summary>
          <p className="mt-2 text-xs text-slate-500">Review for relevance before using; this is not a live status update.</p>
          <p className="mt-2 leading-relaxed">{c.owlbert_summary}</p>
        </details>
      )}

      {/* Action shortcuts — inline gap-driven mini grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        <ActionCell
          icon={MessageSquare}
          label="comments"
          value={c.pending_comments}
          onClick={onFeed}
          testid={`row-comments-${c.workspace_id}`}
        />
        <ActionCell
          icon={Reply}
          label="replies"
          value={c.pending_replies}
          onClick={onReplies}
          testid={`row-replies-${c.workspace_id}`}
        />
        <ActionCell
          icon={Calendar}
          label="overdue"
          value={c.posts_overdue}
          onClick={onPipeline}
          tone={c.posts_overdue > 0 ? "danger" : "muted"}
          testid={`row-overdue-${c.workspace_id}`}
        />
        <ActionCell
          icon={Flame}
          label="hot 7d"
          value={c.hot_signals}
          onClick={onFeed}
          tone={c.hot_signals > 0 ? "warn" : "muted"}
          testid={`row-hot-${c.workspace_id}`}
        />
      </div>

      {/* Setup status row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <SetupDot label="Client profile" done={c.brain?.client} />
          <SetupDot label="Writing voice" done={c.brain?.voice} />
          <SetupDot label="Strategy" done={c.brain?.strategy} />
        </div>
        <div className="flex items-center gap-2">
          {!setupComplete && (
            <button
              onClick={onSetup}
              className="btn-ghost text-xs inline-flex items-center gap-1.5"
              data-testid={`finish-setup-${c.workspace_id}`}
            >
              <Sparkles className="w-3 h-3" />
              Finish setup
            </button>
          )}
          <button
            onClick={onBrain}
            className="btn-ghost text-xs inline-flex items-center gap-1.5"
            data-testid={`open-brain-${c.workspace_id}`}
          >
            <Brain className="w-3 h-3" />
            Memory
          </button>
          <button
            onClick={onOpen}
            className="btn-text inline-flex items-center gap-1"
            data-testid={`open-client-${c.workspace_id}`}
          >
            Engagement <ArrowRight className="w-3 h-3" />
          </button>
          <button
            onClick={async () => {
              try {
                let r = await apiGet(`/workspaces/${c.workspace_id}/portal-token`);
                if (!r.token) {
                  r = await apiPost(`/workspaces/${c.workspace_id}/portal-token`);
                }
                const url = `${window.location.origin}${r.url_path}`;
                await navigator.clipboard.writeText(url);
                toast.success("public link copied", {
                  description: url,
                  duration: 6000,
                  action: { label: "open", onClick: () => window.open(url, "_blank") },
                });
              } catch (e) {
                toast.error("could not create share link");
              }
            }}
            className="btn-text inline-flex items-center gap-1"
            data-testid={`share-portal-${c.workspace_id}`}
            title="Copy the public client portal link (no login required)"
          >
            Share portal <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionCell({ icon: Icon, label, value, onClick, tone = "muted", testid }) {
  const v = value ?? 0;
  const isActive = v > 0;
  const color =
    tone === "danger" ? "#ef4444"
    : tone === "warn" ? "#f59e0b"
    : isActive ? "#111827"
    : "#9ca3af";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-left rounded-xl px-3 py-3 hover:bg-[#f3f4f6] transition-colors"
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-2.5 h-2.5" strokeWidth={1.5} style={{ color }} />
        <span className="stat-label" style={{ fontSize: 9 }}>{label}</span>
      </div>
      <div className="stat-num stat-num-sm mt-1" style={{ color }}>{v}</div>
    </button>
  );
}

function SetupDot({ label, done }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      {done ? (
        <CheckCircle2 className="w-3 h-3 text-sage" style={{ color: "hsl(var(--accent))" }} strokeWidth={1.5} />
      ) : (
        <Circle className="w-3 h-3 text-stone-dim" strokeWidth={1.5} />
      )}
      <span className={`mono text-[10px] uppercase tracking-wider ${done ? "text-stone" : "text-stone-dim"}`}>
        {label}
      </span>
    </div>
  );
}

function EmptyState({ onCreate, canCreate }) {
  return (
    <div className="ed-empty" data-testid="empty-state">
      <img src={OWL_PORTRAIT} alt="Owlbert" />
      <div className="chapter-eyebrow mt-6 mb-3">No clients yet</div>
      <h2 className="chapter-title" style={{ fontSize: 44 }}>Add your first client.</h2>
      <p className="chapter-sub mt-4 mb-8">
        Onboarding takes 4 steps and sets up everything needed to draft in their voice.
      </p>
      {canCreate ? (
        <button
          onClick={onCreate}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="empty-create-workspace"
        >
          <Plus className="w-4 h-4" />
          Onboard a client
        </button>
      ) : (
        <div className="stat-label">contact your admin to be added</div>
      )}
    </div>
  );
}
